import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Camera, ImagePlus, Search, Loader2, Trash2, RefreshCw, ScanLine, X as XIcon, Star, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const DEFAULT_SERVICE_ID = '10';
const FAV_KEY = 'fliptrack_imei_favs';
const LOAD_FAVS = () => {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    if (Array.isArray(arr) && arr.length) return arr.map(String);
  } catch (_e) { /* ignore */ }
  return [DEFAULT_SERVICE_ID]; // start with the user's main service pinned
};

const fmtMoney = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  return `$${v.toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

// Downscale a mobile photo (iPhone shots are 3-12 MB / 3000-4000 px wide) to
// keep the upload + OCR round-trip snappy. Returns a File suitable for POST.
const resizeImageFile = (file, maxDim = 1600, quality = 0.88) => new Promise((resolve) => {
  try {
    if (!file || !file.type?.startsWith('image/')) return resolve(file);
    // No need to resize tiny screenshots
    if (file.size < 400 * 1024) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          const nameBase = file.name?.replace(/\.[^.]+$/, '') || 'photo';
          resolve(new File([blob], `${nameBase}.jpg`, { type: 'image/jpeg' }));
        },
        'image/jpeg', quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  } catch (_e) {
    resolve(file);
  }
});

export default function ImeiCheck() {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState('');

  const [imei, setImei] = useState('');
  const [serviceId, setServiceId] = useState(DEFAULT_SERVICE_ID);
  const [services, setServices] = useState([]);
  const [balance, setBalance] = useState(null);
  const [sickwBlocked, setSickwBlocked] = useState(false);
  const [sickwMsg, setSickwMsg] = useState('');
  const [favIds, setFavIds] = useState(LOAD_FAVS);
  const [serviceFilter, setServiceFilter] = useState('');
  const [customServiceId, setCustomServiceId] = useState('');

  const persistFavs = (next) => {
    setFavIds(next);
    try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch (_e) { /* ignore */ }
  };
  const toggleFav = (id) => {
    const sid = String(id);
    const next = favIds.includes(sid) ? favIds.filter(x => x !== sid) : [...favIds, sid];
    persistFavs(next);
  };

  const [checking, setChecking] = useState(false);
  const [latest, setLatest] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/imei/history`);
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('history load failed', e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchBalance = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/imei/balance`);
      setBalance(r.data?.balance);
      if (r.data?.blocked) { setSickwBlocked(true); setSickwMsg(r.data.detail || ''); }
    } catch (e) {
      setBalance(null);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/imei/services`);
      setServices(Array.isArray(r.data?.services) ? r.data.services : []);
      if (r.data?.default_service_id) setServiceId(r.data.default_service_id);
      if (r.data?.blocked) { setSickwBlocked(true); setSickwMsg(r.data.detail || ''); }
      else if (Array.isArray(r.data?.services) && r.data.services.length > 0) { setSickwBlocked(false); }
    } catch (e) {
      setServices([]);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchBalance();
    fetchServices();
  }, [fetchHistory, fetchBalance, fetchServices]);

  // Object-URL cleanup
  useEffect(() => {
    if (!previewUrl) return undefined;
    return () => { URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const selectedService = useMemo(
    () => services.find(s => s.service_id === serviceId),
    [services, serviceId],
  );

  const favoriteServices = useMemo(() => {
    // Preserve the order the user pinned them in, drop ids no longer in the
    // services list (e.g. if Sickw removes one).
    const map = new Map(services.map(s => [s.service_id, s]));
    return favIds.map(id => map.get(id)).filter(Boolean);
  }, [services, favIds]);

  const filteredServices = useMemo(() => {
    const q = serviceFilter.trim().toLowerCase();
    if (!q) return services;
    return services.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      String(s.service_id).includes(q)
    );
  }, [services, serviceFilter]);

  const handleImageChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    // Shrink the picture in the browser before we ship it up
    const shrunk = await resizeImageFile(f);
    setFileObj(shrunk);
    setPreviewUrl(URL.createObjectURL(shrunk));
    setExtractNote('');
    setLatest(null);

    // Auto-extract immediately
    setExtracting(true);
    const t0 = Date.now();
    try {
      const form = new FormData();
      form.append('file', shrunk);
      const r = await axios.post(`${API}/imei/extract`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const secs = ((Date.now() - t0) / 1000).toFixed(1);
      const found = r.data?.imei || r.data?.serial || '';
      if (found) {
        setImei(found);
        toast.success(`Found ${r.data?.imei ? 'IMEI' : 'Serial'}: ${found} · ${secs}s`);
      } else {
        setExtractNote(r.data?.note || 'No IMEI or Serial found in the photo.');
        toast.message(`No IMEI / Serial found (${secs}s) — type it manually below.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OCR failed');
    } finally {
      setExtracting(false);
      // Reset the file inputs so the same photo can be reselected
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const clearImage = () => {
    setFileObj(null);
    setPreviewUrl(null);
    setExtractNote('');
  };

  const runCheck = async () => {
    const cleaned = (imei || '').trim().replace(/\s+/g, '');
    if (!cleaned) {
      toast.error('Enter (or scan) an IMEI / Serial first');
      return;
    }
    setChecking(true);
    setLatest(null);
    try {
      const r = await axios.post(`${API}/imei/check`, {
        imei: cleaned,
        service_id: serviceId,
      });
      setLatest(r.data);
      if (r.data?.blocked) {
        toast.error(r.data.detail || 'Sickw is blocking this server.');
      } else if (r.data?.status === 'success') {
        toast.success('Check complete');
      } else {
        toast.message(r.data?.detail || `Status: ${r.data?.status || 'unknown'}`);
      }
      fetchHistory();
      fetchBalance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sickw check failed');
    } finally {
      setChecking(false);
    }
  };

  const handleDeleteCheck = async (id) => {
    if (!window.confirm('Delete this saved check?')) return;
    try {
      await axios.delete(`${API}/imei/history/${id}`);
      setHistory(prev => prev.filter(x => x.id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="imei-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-3xl sm:text-5xl text-foreground tracking-wide flex items-center gap-3 flex-wrap">
            <ScanLine className="h-7 w-7 sm:h-8 sm:w-8 text-primary shrink-0" />
            <span className="break-words">IMEI CHECK</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Snap a photo (or pick from your gallery), we&apos;ll pull the IMEI / Serial automatically and run it through Sickw.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm shrink-0">
          <div
            className="px-3 py-1.5 rounded-md bg-card border border-border tabular-nums text-xs sm:text-sm"
            data-testid="sickw-balance"
            title="Your Sickw.com balance"
          >
            Balance: <span className="font-semibold text-foreground">{balance == null ? '—' : fmtMoney(balance)}</span>
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => { fetchBalance(); fetchServices(); fetchHistory(); }}
            data-testid="refresh-imei-btn"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {sickwBlocked && (
        <div
          className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300"
          data-testid="sickw-blocked-notice"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Live balance &amp; service list unavailable on this server.</p>
            <p className="mt-0.5 text-amber-700/90 dark:text-amber-300/80">
              {sickwMsg || "Sickw blocks cloud IPs from loading the balance and live service list, but IMEI/serial checks work fine. Pick any service below (or enter a service ID) and run it — check your credit balance on sickw.com."}
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Scan / pick image */}
        <Card className="border-2 border-foreground/10 min-w-0" data-testid="imei-scan-card">
          <CardHeader>
            <CardTitle className="text-base font-display tracking-wide">SCAN A DEVICE</CardTitle>
            <CardDescription>
              On mobile, &quot;Take Photo&quot; opens the camera. On desktop or to use an existing screenshot, use &quot;Pick from Gallery&quot;.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* hidden file inputs */}
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleImageChosen}
              data-testid="imei-camera-input"
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChosen}
              data-testid="imei-gallery-input"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                variant="default"
                onClick={() => cameraRef.current?.click()}
                disabled={extracting}
                data-testid="imei-take-photo-btn"
                className="w-full"
              >
                <Camera className="h-4 w-4 mr-2" /> Take Photo
              </Button>
              <Button
                variant="outline"
                onClick={() => galleryRef.current?.click()}
                disabled={extracting}
                data-testid="imei-pick-gallery-btn"
                className="w-full"
              >
                <ImagePlus className="h-4 w-4 mr-2" /> Pick from Gallery
              </Button>
            </div>

            {previewUrl && (
              <div className="relative rounded-md overflow-hidden border border-border" data-testid="imei-preview">
                <img src={previewUrl} alt="Selected" className="w-full max-h-72 object-contain bg-black/40" />
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-background/80 hover:bg-background text-foreground flex items-center justify-center border border-border"
                  data-testid="imei-clear-preview"
                  title="Clear photo"
                >
                  <XIcon className="h-4 w-4" />
                </button>
                {extracting && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm text-foreground gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Reading the photo…
                  </div>
                )}
              </div>
            )}

            {extractNote && !extracting && (
              <p className="text-xs text-amber-600 dark:text-amber-500" data-testid="imei-extract-note">
                {extractNote}
              </p>
            )}

            <div className="space-y-2 border-t border-border pt-4">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">IMEI or Serial</Label>
              <Input
                value={imei}
                onChange={e => setImei(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runCheck(); }}
                placeholder="Paste, type, or auto-fill from photo…"
                className="font-mono tabular-nums tracking-wider"
                data-testid="imei-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Service</Label>
                {selectedService && (
                  <button
                    type="button"
                    onClick={() => toggleFav(selectedService.service_id)}
                    className={`text-[11px] flex items-center gap-1 px-1.5 py-0.5 rounded-md border transition-colors ${
                      favIds.includes(selectedService.service_id)
                        ? 'border-amber-500/40 text-amber-600 bg-amber-500/10'
                        : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                    data-testid="imei-toggle-fav"
                    title={favIds.includes(selectedService.service_id) ? 'Remove from quick picks' : 'Pin to quick picks'}
                  >
                    <Star className="h-3 w-3" fill={favIds.includes(selectedService.service_id) ? 'currentColor' : 'none'} />
                    {favIds.includes(selectedService.service_id) ? 'Pinned' : 'Pin'}
                  </button>
                )}
              </div>

              {/* Quick-pick pills — one tap to choose your usual services */}
              {favoriteServices.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="imei-quick-services">
                  {favoriteServices.map(s => {
                    const active = s.service_id === serviceId;
                    return (
                      <button
                        key={s.service_id}
                        type="button"
                        onClick={() => setServiceId(s.service_id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card border-border hover:border-foreground/30'
                        }`}
                        data-testid={`imei-quick-service-${s.service_id}`}
                        title={`${s.name} · ${fmtMoney(s.price)}`}
                      >
                        <Star className="h-3 w-3" fill="currentColor" />
                        <span className="font-semibold tabular-nums">{s.service_id}</span>
                        <span className="opacity-80 truncate max-w-[120px] sm:max-w-[180px]">{s.name}</span>
                        <span className="opacity-70 tabular-nums">{fmtMoney(s.price)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Search box above the full list */}
              {services.length > 0 && (
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={serviceFilter}
                    onChange={e => setServiceFilter(e.target.value)}
                    placeholder="Search all services (carrier, fmi, gsx, samsung…)"
                    className="h-8 pl-7 text-xs"
                    data-testid="imei-service-search"
                  />
                </div>
              )}

              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger data-testid="imei-service-select">
                  <SelectValue placeholder="Pick a service" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {services.length === 0 && (
                    <SelectItem value={DEFAULT_SERVICE_ID}>iPhone Carrier & FMI &amp; Blacklist (default)</SelectItem>
                  )}
                  {favoriteServices.length > 0 && (
                    <>
                      <div className="px-2 pt-1.5 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Pinned</div>
                      {favoriteServices.map(s => (
                        <SelectItem key={`fav-${s.service_id}`} value={s.service_id}>
                          ★ {s.service_id} — {s.name} ({fmtMoney(s.price)})
                        </SelectItem>
                      ))}
                      <div className="my-1 border-t border-border" />
                      <div className="px-2 pt-0.5 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">All services</div>
                    </>
                  )}
                  {filteredServices.map(s => (
                    <SelectItem key={s.service_id} value={s.service_id}>
                      {s.service_id} — {s.name} ({fmtMoney(s.price)})
                    </SelectItem>
                  ))}
                  {filteredServices.length === 0 && services.length > 0 && (
                    <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                      No service matches “{serviceFilter}”
                    </div>
                  )}
                </SelectContent>
              </Select>
              {selectedService && (
                <p className="text-[11px] text-muted-foreground">
                  This check will cost <span className="font-semibold text-foreground">{fmtMoney(selectedService.price)}</span>.
                </p>
              )}
              {!selectedService && serviceId && (
                <p className="text-[11px] text-muted-foreground">
                  Using service <span className="font-semibold text-foreground">#{serviceId}</span>.
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Input
                  value={customServiceId}
                  onChange={e => setCustomServiceId(e.target.value.replace(/[^0-9]/g, ''))}
                  onKeyDown={e => { if (e.key === 'Enter' && customServiceId) setServiceId(customServiceId); }}
                  placeholder="Or type any service ID"
                  className="h-8 text-xs w-44"
                  inputMode="numeric"
                  data-testid="imei-custom-service-input"
                />
                <Button
                  size="sm" variant="outline" className="h-8"
                  disabled={!customServiceId}
                  onClick={() => setServiceId(customServiceId)}
                  data-testid="imei-custom-service-apply"
                >
                  Use ID
                </Button>
              </div>
            </div>

            <Button
              onClick={runCheck}
              disabled={checking || !imei.trim()}
              className="w-full"
              data-testid="imei-run-check-btn"
            >
              {checking
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking…</>
                : <><Search className="h-4 w-4 mr-2" /> Run Check</>}
            </Button>
          </CardContent>
        </Card>

        {/* Latest result */}
        <Card className="border-2 border-foreground/10 min-w-0" data-testid="imei-result-card">
          <CardHeader>
            <CardTitle className="text-base font-display tracking-wide">LATEST RESULT</CardTitle>
            <CardDescription className="break-words">
              {latest
                ? `${latest.device_label ? latest.device_label + ' · ' : ''}IMEI ${latest.imei} · service ${latest.service_id}${latest.service_name ? ` · ${latest.service_name}` : ''}`
                : 'Run a check to see the result here.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!latest ? (
              <div className="h-48 flex items-center justify-center text-sm text-muted-foreground border-2 border-dashed border-border rounded-md">
                Nothing yet.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="secondary"
                    className={
                      latest.status === 'success'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : latest.status === 'error'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-slate-500/10 text-slate-600'
                    }
                    data-testid="imei-result-status"
                  >
                    {String(latest.status || 'unknown').toUpperCase()}
                  </Badge>
                  {latest.price ? (
                    <span className="text-xs text-muted-foreground">Cost: {fmtMoney(latest.price)}</span>
                  ) : null}
                  {latest.order_id && (
                    <span className="text-xs text-muted-foreground">Order #{latest.order_id}</span>
                  )}
                </div>
                <pre
                  className="text-xs sm:text-sm whitespace-pre-wrap break-words font-mono bg-muted/40 rounded-md p-3 border border-border max-w-full overflow-x-auto"
                  data-testid="imei-result-text"
                >
{latest.result || '(empty result)'}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card className="border-2 border-foreground/10" data-testid="imei-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display tracking-wide">CHECK HISTORY</CardTitle>
          <CardDescription>
            Every check you&apos;ve run is saved here automatically. Click any row to load its result.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="py-10 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : history.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No checks yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(row => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setLatest(row)}
                      data-testid={`imei-history-row-${row.id}`}
                    >
                      <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5" data-testid={`imei-history-device-${row.id}`}>
                          <span className="font-semibold text-foreground text-sm">
                            {row.device_label || <span className="text-muted-foreground italic">Unknown model</span>}
                          </span>
                          <span
                            className="font-mono text-[11px] text-muted-foreground tabular-nums"
                            title={row.imei}
                          >
                            {row.imei}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.service_id}{row.service_name ? ` · ${row.service_name}` : ''}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={
                            row.status === 'success'
                              ? 'bg-emerald-500/10 text-emerald-600'
                              : row.status === 'error'
                                ? 'bg-destructive/10 text-destructive'
                                : 'bg-slate-500/10 text-slate-600'
                          }
                        >
                          {String(row.status || 'unknown').toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.price ? fmtMoney(row.price) : '—'}</TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCheck(row.id); }}
                          data-testid={`imei-delete-${row.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
